import * as React from 'react';

import { ChevronDown, LucideLoader } from 'lucide-react';
import { ValidationResult } from 'react-aria-components';
import useResizeObserver from 'use-resize-observer';

import { useTranslations } from '../../../../i18n/useTranslations';
import { cn, withColor } from '../../../../lib/stylingUtils';
import { ColorScheme, ThemeColor } from '../../../../types';
import {
  FieldError,
  FormDescription,
  Label,
  fieldGroupVariants,
} from '../../Field';
import { Popover } from '../../overlays/Popover';
import { sortByGroup } from '../sortByGroup';
import { useSelectHelpers } from '../useSelectHelpers';

import { TouchSelectOption } from './TouchSelectOption';
import { useTouchSelectNavigation } from './useTouchSelectNavigation';
import { FilteredOption, useTouchSelectSearch } from './useTouchSelectSearch';

export interface TouchSelectProps<ValueType extends string | number = string> {
  onChange?: (key: ValueType) => void;
  value?: ValueType | undefined | null;
  placeholder?: string;
  isDisabled?: boolean;
  options?: {
    label: React.ReactNode;
    subLabel?: React.ReactNode;
    extraInfo?: React.ReactNode;
    group?: string;
    selectedLabel?: string;
    searchable?: string;
    key: ValueType;
    isDisabled?: boolean;
  }[];
  /** Controlled query value for remote search scenarios */
  query?: string;
  /** Callback when query changes - enables controlled query mode for remote search */
  onChangeQuery?: (query: string) => void;
  isLoading?: boolean;
  className?: string;
  themeMode?: ColorScheme;
  color?: ThemeColor;
  variant?: 'flat' | 'outlined';
  size?: 'xl' | 'base' | 'xs';
  getSelection?: (ids: ValueType) => {
    label: React.ReactNode;
    subLabel?: React.ReactNode;
  };
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  /** Optional prefix to display on the left side of the input. When present, the selected value will be right-aligned. */
  prefix?: React.ReactNode;
  /** When provided, overrides the default trigger click behavior (opening the popover). The trigger will call this function instead. */
  onTriggerClick?: () => void;
  label?: React.ReactNode;
  labelAdornment?: React.ReactNode;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string | ((validation: ValidationResult) => string);
  description?: string;
  /** When true, the search input is hidden and searching is disabled. */
  hideSearch?: boolean;
  /** When true, a chevron icon is displayed that toggles based on the menu's visibility. */
  showChevron?: boolean;
  renderEmpty?: (query: string) => React.ReactNode;
  'aria-label'?: string;
}

type GroupedOption = {
  key: string;
  items: { option: FilteredOption; globalIndex: number }[];
};

type TouchSelectGroupedOptionsProps = {
  groupedOptions: GroupedOption[];
  currentSelectionIndex: number | undefined;
  currentFocusIndex: number;
  isKeyboardNavigating: boolean;
  onSelect: (index: number) => void;
  onHover: (index: number) => void;
};

const TouchSelectGroupedOptions: React.FC<TouchSelectGroupedOptionsProps> = ({
  groupedOptions,
  currentSelectionIndex,
  currentFocusIndex,
  isKeyboardNavigating,
  onSelect,
  onHover,
}) => (
  <>
    {groupedOptions.map((group) => (
      <div key={group.key || 'default'}>
        {group.key && (
          <div className="mt-2 mb-1 px-3 text-xs font-semibold tracking-wider text-neutral/60 uppercase">
            {group.key} ({group.items.length})
          </div>
        )}
        {group.items.map(({ option, globalIndex }) => (
          <TouchSelectOption
            key={option?.id}
            label={option?.label}
            subLabel={option.subLabel}
            rightInfo={option.rightInfo}
            isDisabled={option.isDisabled}
            isSelected={currentSelectionIndex === globalIndex}
            isFocused={currentFocusIndex === globalIndex}
            isKeyboardNavigating={isKeyboardNavigating}
            onSelect={() => onSelect(globalIndex)}
            onHover={() => onHover(globalIndex)}
          />
        ))}
      </div>
    ))}
  </>
);

export const TouchSelect = <ValueType extends string | number = string>({
  options,
  isDisabled,
  onChange: onSelectionChange,
  placeholder = 'Select',
  onFocus,
  themeMode = 'light',
  query: controlledQuery,
  getSelection,
  onChangeQuery,
  onBlur,
  prefix,
  variant = 'flat',
  color,
  onTriggerClick,
  label,
  labelAdornment,
  isRequired,
  isInvalid: isInvalidProp,
  errorMessage,
  description,
  size = 'base',
  hideSearch = false,
  showChevron,
  renderEmpty,
  'aria-label': ariaLabel,
  ...props
}: TouchSelectProps<ValueType>) => {
  const t = useTranslations();

  const isInvalid = isInvalidProp || !!errorMessage;

  const optionsContainerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const lastMousePositionRef = React.useRef({ x: 0, y: 0 });

  const [focusLocked, setFocusLocked] = React.useState(false);
  const [isMenuVisible, setIsMenuVisible] = React.useState(false);
  const [currentFocusIndex, setCurrentFocusIndex] = React.useState(-1);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = React.useState(false);

  const { ref: inputContainerRef, width: inputContainerWidth } =
    useResizeObserver();
  const { width: fieldWidth } = useResizeObserver({
    ref: rootRef as never,
  });
  const { extractTextContentFromReactNode } = useSelectHelpers();
  const {
    query: internalQuery,
    setQuery: setInternalQuery,
    clearQuery: clearInternalQuery,
    filteredOptions: localFilteredOptions,
  } = useTouchSelectSearch({
    options,
    extractTextContentFromReactNode,
  });

  const effectiveSelectedKey = props.value;
  const isControlledQuery = controlledQuery !== undefined;
  const query = isControlledQuery ? controlledQuery : internalQuery;

  const filteredOptions = React.useMemo(() => {
    const mapped = isControlledQuery
      ? (options?.map((it) => ({
          id: it.key,
          label: it.label,
          subLabel: it.subLabel,
          rightInfo: it.extraInfo,
          group: it.group,
          isDisabled: it.isDisabled,
        })) ?? [])
      : localFilteredOptions;

    return sortByGroup(mapped);
  }, [isControlledQuery, options, localFilteredOptions]);

  const currentSelectionIndex = React.useMemo(
    () => filteredOptions.findIndex((it) => it.id === effectiveSelectedKey),
    [filteredOptions, effectiveSelectedKey],
  );

  const groupedOptions = React.useMemo(() => {
    const groups: GroupedOption[] = [];
    const groupMap = new Map<
      string,
      { option: FilteredOption; globalIndex: number }[]
    >();

    filteredOptions.forEach((option, globalIndex) => {
      const groupKey = option.group ?? '';
      let items = groupMap.get(groupKey);
      if (!items) {
        items = [];
        groupMap.set(groupKey, items);
        groups.push({ key: groupKey, items });
      }
      items.push({ option, globalIndex });
    });

    return groups;
  }, [filteredOptions]);

  const renderableSelection = React.useMemo(() => {
    if (effectiveSelectedKey && getSelection) {
      const selection = getSelection(effectiveSelectedKey);
      const label = selection?.label || <>&nbsp;</>;
      const subLabel = selection?.subLabel;

      return (
        <>
          <div>{label}</div>
          {subLabel && (
            <div className="text-sm text-neutral/40">{subLabel}</div>
          )}
        </>
      );
    }

    if (effectiveSelectedKey && !getSelection) {
      const matched = options?.find(
        (option) => option.key === effectiveSelectedKey,
      );
      const label = matched?.label ?? <>&nbsp;</>;
      const subLabel = matched?.subLabel;

      return (
        <>
          <div>{label}</div>
          {subLabel && (
            <div className="text-sm text-neutral/40">{subLabel}</div>
          )}
        </>
      );
    }

    return (
      <span data-role="placeholder" className="text-neutral/40">
        {placeholder || <>&nbsp;</>}
      </span>
    );
  }, [effectiveSelectedKey, getSelection, options, placeholder]);

  const setQuery = React.useCallback(
    (newQuery: string) => {
      if (isControlledQuery) {
        onChangeQuery?.(newQuery);
      } else {
        setInternalQuery(newQuery);
      }
    },
    [isControlledQuery, onChangeQuery, setInternalQuery],
  );

  const clearQuery = React.useCallback(() => {
    if (isControlledQuery) {
      onChangeQuery?.('');
    } else {
      clearInternalQuery();
    }
  }, [isControlledQuery, onChangeQuery, clearInternalQuery]);

  const handleNewSelection = React.useCallback(
    (index = currentFocusIndex) => {
      const focusedOption = filteredOptions[index];

      onSelectionChange?.(focusedOption?.id as ValueType);
      setTimeout(() => {
        setIsMenuVisible(false);
        clearQuery();
      });
    },
    [currentFocusIndex, filteredOptions, onSelectionChange, clearQuery],
  );

  const { handleSearchKeydown } = useTouchSelectNavigation({
    isMenuVisible,
    setIsMenuVisible,
    filteredOptions,
    currentFocusIndex,
    setCurrentFocusIndex,
    setIsKeyboardNavigating,
    handleNewSelection,
    triggerRef,
    optionsContainerRef,
    lastMousePositionRef,
  });

  const handleInputFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!hideSearch) {
        searchInputRef.current?.select();
      }
      if (!isDisabled) {
        setIsMenuVisible(true);
      }
      onFocus?.(e);
    },
    [onFocus, isDisabled, hideSearch],
  );

  const handleTriggerClick = React.useCallback(() => {
    if (isDisabled) return;

    if (onTriggerClick) {
      onTriggerClick();
      return;
    }

    if (!focusLocked && !isMenuVisible) {
      setIsMenuVisible(true);
      setTimeout(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [isDisabled, onTriggerClick, hideSearch, focusLocked, isMenuVisible]);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isDisabled) return;
      e.preventDefault();
    },
    [isDisabled],
  );

  const handleBlurMenu = React.useCallback(() => {
    setTimeout(() => setIsMenuVisible(false));
  }, []);

  const handleRootMouseDown = React.useCallback(() => {
    if (isDisabled || onTriggerClick) return;

    setTimeout(() => {
      searchInputRef.current?.focus();
    });
  }, [isDisabled, onTriggerClick]);

  const handleQueryChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (hideSearch) return;
      setQuery(e.target.value);
      setCurrentFocusIndex(0);
    },
    [setQuery, hideSearch],
  );

  React.useEffect(() => {
    if (isMenuVisible) {
      setCurrentFocusIndex(currentSelectionIndex ?? 0);
    }
  }, [isMenuVisible, currentSelectionIndex]);

  React.useLayoutEffect(() => {
    if (isMenuVisible) {
      searchInputRef.current?.focus();
    } else {
      clearQuery();
    }
  }, [isMenuVisible, clearQuery]);

  return (
    <div className="@TouchSelect contents">
      <div
        ref={rootRef}
        className={cn(
          '@TouchableSelect relative flex w-full flex-col gap-2 outline-0',
          props.className,
        )}
        onMouseDown={handleRootMouseDown}
      >
        {label && (
          <Label
            aria-required={isRequired}
            adornment={labelAdornment}
            className={cn(isInvalid && 'text-failure')}
          >
            {label}
          </Label>
        )}
        <div
          ref={triggerRef}
          style={withColor(color ?? 'neutral')}
          className={cn(
            '@Trigger flex h-full w-full cursor-default items-center justify-between rounded-md px-3 py-2 text-sm outline-0',
            isMenuVisible &&
              size !== 'xs' &&
              fieldGroupVariants({
                disabled: isDisabled,
              }),
            isMenuVisible &&
              size === 'xs' &&
              'border border-primary ring-[3px] ring-primary/20',
            size === 'xs' && 'h-9 max-h-9 min-h-9 text-xs',
            size === 'base' && 'min-h-11',
            size === 'xl' && 'h-13 min-h-13 text-lg',
            isMenuVisible && 'bg-white',
            variant === 'outlined' && 'border-2',
            !isMenuVisible &&
              (variant !== 'outlined' || !isDisabled) &&
              'text-custom-color',
            !isMenuVisible &&
              variant === 'outlined' &&
              isDisabled &&
              'border-transparent bg-neutral/10',
            !isMenuVisible &&
              variant === 'outlined' &&
              !isDisabled &&
              'border-custom-color/20',
            !isMenuVisible &&
              variant === 'outlined' &&
              !isDisabled &&
              !color &&
              'bg-white',
            !isMenuVisible &&
              variant === 'outlined' &&
              !isDisabled &&
              color &&
              'bg-custom-color/10',
            !isMenuVisible && variant !== 'outlined' && 'border-none',
            !isMenuVisible &&
              variant !== 'outlined' &&
              !color &&
              'bg-neutral/5',
            !isMenuVisible &&
              variant !== 'outlined' &&
              color &&
              'bg-custom-color/10',
            isDisabled && 'cursor-not-allowed opacity-50',
            isInvalid &&
              'border border-failure focus-within:border-failure focus-within:ring-failure/20',
          )}
          onClick={handleTriggerClick}
          onMouseDown={handleMouseDown}
        >
          <div
            ref={inputContainerRef}
            className={cn(
              'relative flex w-full',
              prefix && 'flex-col justify-center',
              !prefix && 'items-center',
              color === 'failure' && 'text-failure',
            )}
          >
            {prefix && (
              <span className="shrink-0 text-xs leading-tight text-neutral/60">
                {prefix}
              </span>
            )}
            <div
              className={cn(
                'truncate font-normal',
                isMenuVisible && !hideSearch && 'opacity-0',
                !prefix && 'w-0 grow',
              )}
            >
              {renderableSelection}
            </div>
            {!onTriggerClick && (
              <input
                ref={searchInputRef}
                className={cn(
                  'absolute top-0 left-0 h-full w-full cursor-default border-none bg-transparent p-0 text-sm text-neutral outline-none',
                  isDisabled && 'cursor-not-allowed',
                  (!isMenuVisible || hideSearch) &&
                    'pointer-events-none opacity-0',
                  prefix && 'bottom-0 left-0',
                  color === 'failure' && 'text-failure',
                  size === 'xs' && 'text-xs',
                  size === 'xl' && 'text-lg',
                )}
                style={{ width: inputContainerWidth ?? 0 }}
                type="text"
                readOnly={hideSearch}
                value={query}
                onFocus={handleInputFocus}
                onChange={handleQueryChange}
                onBlur={(e) => {
                  setIsMenuVisible(false);
                  onBlur?.(e);
                }}
                onKeyDown={handleSearchKeydown}
                disabled={isDisabled}
              />
            )}
          </div>
          {showChevron && (
            <ChevronDown
              className={cn(
                'size-4 transition-transform duration-200',
                isMenuVisible && 'rotate-180',
                color === 'failure' && 'text-failure',
                !color && 'text-neutral/40',
              )}
            />
          )}
          {props.isLoading && (
            <LucideLoader
              className={'size-[1.125em] animate-spin text-neutral/40'}
            />
          )}
        </div>
        {errorMessage && <FieldError>{errorMessage}</FieldError>}
        {description && <FormDescription>{description}</FormDescription>}
      </div>

      {!onTriggerClick && (
        <Popover
          aria-label={
            ariaLabel ??
            (typeof label === 'string' ? label : undefined) ??
            placeholder
          }
          triggerRef={triggerRef}
          isOpen={!isDisabled && isMenuVisible}
          onOpenChange={(isOpen) => {
            setIsMenuVisible(isOpen);
            setFocusLocked(true);
            setTimeout(() => setFocusLocked(false), 500);
          }}
          placement="bottom left"
          className={cn(
            'no-scrollbar ml-[2px] flex flex-col gap-1 overflow-y-auto',
            'rounded-md border-[1px] border-solid border-neutral/10',
            'bg-white bg-clip-padding p-1 text-white shadow-xl',
            'z-30 mt-1 overflow-clip',
            themeMode === 'dark' && 'light',
            themeMode !== 'dark' && 'dark',
          )}
          isNonModal
          offset={2}
        >
          <div
            tabIndex={-1}
            onFocus={() => setIsMenuVisible(true)}
            onBlur={handleBlurMenu}
            className={
              'no-scrollbar box-border flex h-full max-h-[40vh] min-w-70 flex-col overflow-y-auto'
            }
            style={{ width: fieldWidth ? fieldWidth : 'auto' }}
          >
            {(query ||
              !!groupedOptions.length ||
              (isControlledQuery && !props.isLoading)) && (
              <div className="px-0.5 pt-[1px] pb-0.5" ref={optionsContainerRef}>
                <TouchSelectGroupedOptions
                  groupedOptions={groupedOptions}
                  currentSelectionIndex={currentSelectionIndex}
                  currentFocusIndex={currentFocusIndex}
                  isKeyboardNavigating={isKeyboardNavigating}
                  onSelect={handleNewSelection}
                  onHover={setCurrentFocusIndex}
                />

                {!filteredOptions.length &&
                  !props.isLoading &&
                  (renderEmpty ? (
                    renderEmpty(controlledQuery ?? '')
                  ) : (
                    <div className="mt-1 px-3 py-2.5 pr-6 text-sm text-neutral/60">
                      {t.components.select.noOptions}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Popover>
      )}
    </div>
  );
};
